import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import React, { useState } from "react";

import { ButtonVariant, PMButton } from "../pm-button/pm-button";

interface EventPayload {
  customCheckboxChecked?: boolean;
}

/**
 * Props for the PMPopUp component
 */
export interface PMPopUpProps {
  title: string;
  description?: string | React.ReactNode;
  onClose?: () => void;
  open?: boolean;
  customButtonText?: string;
  customButtonVariant?: "contained" | "outlined" | "text";
  customButtonColor?:
    "primary" | "secondary" | "error" | "warning" | "info" | "success";
  customButtonFireEvent?: (eventName: string, data?: EventPayload) => void;
  confirmationButtonText?: string;
  confirmationButtonVariant?: "contained" | "outlined" | "text";
  confirmationButtonColor?: ButtonVariant;
  confirmationButtonFireEvent?: (
    eventName: string,
    data?: EventPayload
  ) => void;
  confirmationButtonDisabled?: boolean;
  cancelButtonText?: string;
  cancelButtonVariant?: "contained" | "outlined" | "text";
  cancelButtonColor?: ButtonVariant;
  cancelButtonFireEvent?: (eventName: string, data?: EventPayload) => void;
  cancelButtonDisabled?: boolean;
  customCheckboxText?: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  showCloseButton?: boolean;
  children?: React.ReactNode;
  /** Forwarded to the underlying MUI Dialog — use e.g. slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }} to suppress a double-backdrop inside another Dialog. */
  slotProps?: React.ComponentProps<typeof Dialog>["slotProps"];
}

/**
 * A reusable popup/modal dialog component for PM applications with three optional buttons and sensible defaults.
 *
 * Features:
 * - Self-managing: Automatically handles open/close state internally
 * - External control: Optional `open` prop for external state management
 * - Three optional buttons: Custom (left), Cancel and Confirm (right side)
 * - Sensible defaults: Pre-configured with standard UI patterns
 * - Responsive: Configurable max width and full width options
 *
 * @example
 * ```tsx
 * // Basic usage with self-management
 * <PMPopUp
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   confirmationButtonText="Yes"
 *   cancelButtonText="No"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // External control with custom styling
 * <PMPopUp
 *   open={isOpen}
 *   title="Custom Dialog"
 *   onClose={() => setIsOpen(false)}
 *   customButtonText="Custom Action"
 *   customButtonVariant="outlined"
 *   customButtonColor="secondary"
 *   maxWidth="lg"
 * />
 * ```
 *
 * @param title - The title displayed in the popup header
 * @param description - Optional description text displayed in the popup body
 * @param onClose - Callback function called when the popup is closed
 * @param open - Controls whether the popup is open (for external control)
 * @param customButtonText - Text for the custom button (left side)
 * @param customButtonVariant - Variant style for the custom button
 * @param customButtonColor - Color theme for the custom button
 * @param customButtonFireEvent - Event handler for the custom button click
 * @param confirmationButtonColor - Color theme for the confirmation button
 * @param confirmationButtonFireEvent - Event handler for the confirmation button click
 * @param cancelButtonText - Text for the cancel button (right side)
 * @param cancelButtonFireEvent - Event handler for the cancel button click
 * @param cancelButtonColor - Color theme for the cancel button
 * @param customCheckboxText - Optional text for a checkbox
 * @param maxWidth - Maximum width of the popup dialog
 * @param fullWidth - Whether the popup should take full width
 * @param showCloseButton - Whether to show a close button in the header
 * @returns A React dialog component
 */
export const PMPopUp: PimoReactComponent<PMPopUpProps> = ({
  title,
  description,
  onClose,
  open,
  customButtonText,
  customButtonVariant = "outlined",
  customButtonColor = "primary",
  customButtonFireEvent,
  confirmationButtonText,
  confirmationButtonFireEvent,
  confirmationButtonDisabled = false,
  confirmationButtonColor,
  cancelButtonText,
  cancelButtonFireEvent,
  cancelButtonDisabled = false,
  cancelButtonColor,
  customCheckboxText,
  maxWidth = "sm",
  fullWidth = true,
  showCloseButton = true,
  children,
  slotProps,
}) => {
  const theme = useTheme();
  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = open !== undefined ? open : internalOpen;
  const customCheckboxRef = React.useRef<HTMLInputElement>(null);
  const hasButtons =
    !!customButtonText || !!confirmationButtonText || !!cancelButtonText;

  const handleClose = () => {
    if (open === undefined) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      slotProps={slotProps}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: description ? 200 : 150,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "64px",
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "100%",
            letterSpacing: "0%",
            verticalAlign: "middle",
          }}
        >
          {title}
        </Typography>
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{ pt: 1, pb: 0, ...(hasButtons ? {} : { marginBottom: 6 }) }}
      >
        {description && (
          <Typography
            variant="body1"
            color={theme.palette.text.secondary}
            sx={{ fontSize: "14px" }}
          >
            {description}
          </Typography>
        )}
        {children}
      </DialogContent>

      {hasButtons && (
        <DialogActions
          sx={{
            px: "24px",
            py: "24px",
            justifyContent: "space-between",
            gap: "104px",
            borderRadius: "12px",
          }}
        >
          {customCheckboxText && (
            <FormControlLabel
              control={
                <Checkbox slotProps={{ input: { ref: customCheckboxRef } }} />
              }
              label={customCheckboxText}
            />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            {customButtonText && (
              <Button
                variant={customButtonVariant}
                color={customButtonColor}
                onClick={() => customButtonFireEvent?.("customButtonClick")}
                sx={{ textTransform: "none" }}
              >
                {customButtonText}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {cancelButtonText && (
              <PMButton
                size="medium"
                variant={
                  cancelButtonDisabled
                    ? "disabled"
                    : (cancelButtonColor ?? "contained")
                }
                text={cancelButtonText}
                onClick={() => cancelButtonFireEvent?.("cancelButtonClick")}
              />
            )}
            {confirmationButtonText && (
              <PMButton
                size="medium"
                variant={
                  confirmationButtonDisabled
                    ? "disabled"
                    : (confirmationButtonColor ?? "outlined")
                }
                text={confirmationButtonText}
                onClick={() => {
                  const checked = customCheckboxRef.current?.checked;

                  if (!!customCheckboxText) {
                    confirmationButtonFireEvent?.("confirmationButtonClick", {
                      customCheckboxChecked: checked ?? false,
                    });

                    return;
                  }
                  confirmationButtonFireEvent?.("confirmationButtonClick");
                }}
              />
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PMPopUp;
